import React, { useState, useEffect, useRef } from 'react';
import { Trophy, ChevronLeft, Zap, Crown, Gift, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/feedback';
import { 
  subscribeToBossById, 
  subscribeToTopHunters, 
  subscribeToUserParticipation, 
  queueTapDamage, 
  claimBossReward, 
  getBossCycleInfo,
  BOSS_TEMPLATES,
  renderBossTitle
} from '../services/bossService';

export default function TestBossV3({ currentProfile }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userName = currentProfile?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Athlète';

  // Cible : MusculaThor (boss spécifique de l'arène /test3 basé sur le temps de séance)
  const TARGET_BOSS_ID = 'golem';

  // --- ÉTATS TEMPS RÉEL FIRESTORE ---
  const [boss, setBoss] = useState(BOSS_TEMPLATES.golem || {
    id: 'golem',
    name: 'MusculaThor',
    title: 'Le Dieu de la ~~Foudre~~ Fonte',
    description: "Colosse de pierre titanesque aux veines telluriques arcaniques. Seule une endurance acharnée dans la salle permet d'éroder sa cuirasse rocheuse.",
    taunt: "Ceinture, sangles, magnésie... tout ce cinéma pour une barre aussi légère ?",
    ruleType: 'duration_minutes',
    ruleLabel: '1h30 de séance = 1 300 dégâts',
    ruleDescription: "Chaque minute active passée sous la fonte affaiblit le Golem (90 min = 1 300 dégâts).",
    damageUnit: 'dégâts',
    maxHp: 12000,
    currentHp: 6482,
    status: 'active'
  });
  const [topHunters, setTopHunters] = useState([]);
  const [userPart, setUserPart] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);

  // --- ÉTATS LOCAUX OPTIMISTES (RÉACTIVITÉ INSTANTANÉE SANS AUCUNE LATENCE) ---
  const [localBonusDamage, setLocalBonusDamage] = useState(0);
  const [localHpOffset, setLocalHpOffset] = useState(0);

  // Animations & Effets Visuels
  const [isHit, setIsHit] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [floatingDamages, setFloatingDamages] = useState([]);
  const nextId = useRef(0);

  // 1. Écoute de MusculaThor dans Firestore
  useEffect(() => {
    const unsub = subscribeToBossById(TARGET_BOSS_ID, (activeBoss) => {
      if (activeBoss) {
        setBoss(activeBoss);
      }
    });
    return () => unsub();
  }, []);

  // 2. Écoute du Top Chasseurs pour MusculaThor
  useEffect(() => {
    const unsub = subscribeToTopHunters(TARGET_BOSS_ID, (hunters) => {
      setTopHunters(hunters);
    }, 3);
    return () => unsub();
  }, []);

  // 3. Écoute de la Participation de l'Utilisateur
  useEffect(() => {
    if (!currentProfile?.id) return;
    const unsub = subscribeToUserParticipation(TARGET_BOSS_ID, currentProfile.id, (part) => {
      setUserPart(part);
    });
    return () => unsub();
  }, [currentProfile?.id]);

  // Réconciliation fluide de l'optimistic UI quand Firestore confirme l'écriture
  const prevServerDamage = useRef(null);
  const prevServerHp = useRef(null);

  useEffect(() => {
    if (userPart?.damage !== undefined) {
      if (prevServerDamage.current !== null) {
        const delta = (userPart.damage || 0) - prevServerDamage.current;
        if (delta > 0) {
          setLocalBonusDamage(prev => Math.max(0, prev - delta));
        }
      }
      prevServerDamage.current = userPart.damage || 0;
    }
  }, [userPart?.damage]);

  useEffect(() => {
    if (boss?.currentHp !== undefined) {
      if (prevServerHp.current !== null) {
        const delta = prevServerHp.current - boss.currentHp;
        if (delta > 0) {
          setLocalHpOffset(prev => Math.max(0, prev - delta));
        }
      }
      prevServerHp.current = boss.currentHp;
    }
  }, [boss?.currentHp]);

  const maxHp = boss.maxHp || 12000;
  const baseHp = boss.currentHp !== undefined ? boss.currentHp : 12000;
  const currentHp = Math.max(0, baseHp - localHpOffset);
  const hpPercent = Math.max(0, Math.min(100, (currentHp / maxHp) * 100));
  const bossCycle = boss?.cycleInfo || getBossCycleInfo();
  const isDead = currentHp <= 0 || boss.status === 'defeated';
  const isRaidExpired = boss.status === 'expired' || (!isDead && bossCycle.isRestPeriod);
  const isCombatOver = isDead || isRaidExpired;
  const isLowHp = currentHp > 0 && hpPercent <= 10 && !isCombatOver;
  const rewardCoins = boss.rewardCoins || 150;
  const damageUnit = boss?.damageUnit || 'dégâts';
  const myDamage = (userPart?.damage || 0) + localBonusDamage;
  const userRank = userPart?.rank || (myDamage > 0 ? 1 : null);
  const hasEligibleWorkouts = (userPart?.workoutsCount || 0) >= 1;

  // Nettoyage fluide des dégâts flottants
  useEffect(() => {
    if (floatingDamages.length === 0) return;
    const timer = setTimeout(() => {
      setFloatingDamages(prev => prev.slice(1));
    }, 900);
    return () => clearTimeout(timer);
  }, [floatingDamages]);

  // Déclenchement visuel d'une attaque
  const dealDamage = (amount, isCrit = false, clickX = null, clickY = null, isTap = false) => {
    setIsHit(true);
    setTimeout(() => setIsHit(false), isTap ? 120 : 200);

    if (amount >= 5000) {
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 350);
    }

    const id = nextId.current++;
    const x = clickX !== null ? clickX : Math.random() * 80 - 40;
    const y = clickY !== null ? clickY : Math.random() * 40 - 20;

    setFloatingDamages(prev => [
      ...prev,
      {
        id,
        amount,
        isCrit,
        isTap,
        x,
        y,
        text: isTap ? `-1 ${damageUnit} 👊` : `-${amount.toLocaleString()} ${damageUnit}${isCrit ? ' 💥' : ''}`
      }
    ]);
  };

  // Clic/Tap direct sur l'illustration de MusculaThor
  const handleBossTap = (e) => {
    if (isCombatOver) {
      if (isRaidExpired && !isDead) {
        toast.info("L'arène est en pause. Le prochain boss arrivera lundi prochain !");
      }
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left - rect.width / 2;
    const clickY = e.clientY - rect.top - rect.height / 2;

    dealDamage(1, false, clickX, clickY, true);

    // Optimistic UI immédiate
    setLocalBonusDamage(prev => prev + 1);
    setLocalHpOffset(prev => Math.min(maxHp, prev + 1));

    // Queue vers Firestore avec buffer debounce 1.5s
    if (currentProfile) {
      queueTapDamage(TARGET_BOSS_ID, currentProfile, 1, (err) => {
        console.error("Erreur tap damage MusculaThor:", err);
      }, TARGET_BOSS_ID);
    }
  };

  // Récupération de la récompense de 150 pièces
  const handleClaimReward = async () => {
    if (!currentProfile?.id) {
      toast.error("Sélectionne un profil pour récupérer ton butin.");
      return;
    }
    setIsClaiming(true);
    try {
      const res = await claimBossReward(currentProfile, TARGET_BOSS_ID);
      toast.success(`🎉 Félicitations ! +${res.rewardCoins} Pièces ajoutées à ton compte !`, { icon: '🪙' });
    } catch (err) {
      toast.error(err.message || "Erreur lors de la récupération du butin.");
    } finally {
      setIsClaiming(false);
    }
  };

  // --- CARTES DE DROITE (EXACTEMENT HARMONISÉES AVEC DRACOFONTE ET LE BARROMANCIEN) ---
  const renderContributionCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.75rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
          <Zap size={16} color="var(--primary-color, #8c7fbf)" /> Ta Contribution au Raid
        </div>
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 800,
          color: userRank ? 'var(--secondary-color, #6eb583)' : 'var(--text-secondary, #9a94ab)',
          backgroundColor: userRank ? 'rgba(110, 181, 131, 0.12)' : 'rgba(255, 255, 255, 0.05)',
          padding: '0.15rem 0.5rem',
          borderRadius: '5px'
        }}>
          {userRank ? `Rang #${userRank}` : 'Non classé'}
        </span>
      </div>

      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)', lineHeight: 1 }}>
          {myDamage.toLocaleString()} {damageUnit}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', marginTop: '0.25rem' }}>
          {userPart?.workoutsCount || 0} séance(s) comptabilisée(s) cette semaine
        </div>
      </div>

      {/* BUTIN COLLECTIF */}
      {isDead ? (
        userPart?.hasClaimedReward ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.4rem',
            backgroundColor: 'rgba(110, 181, 131, 0.18)',
            border: '1px solid var(--secondary-color, #6eb583)',
            padding: '0.6rem 0.8rem',
            borderRadius: '10px',
            fontSize: '0.78rem',
            color: 'var(--secondary-color, #6eb583)',
            fontWeight: 800
          }}>
            <Check size={16} /> Butin de {rewardCoins} 🪙 récupéré !
          </div>
        ) : (
          <button
            onClick={handleClaimReward}
            disabled={isClaiming}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              background: 'linear-gradient(135deg, #df9b3e 0%, #bd7b24 100%)',
              color: '#ffffff',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              fontSize: '0.85rem',
              fontWeight: 800,
              cursor: isClaiming ? 'wait' : 'pointer',
              opacity: isClaiming ? 0.7 : 1,
              boxShadow: '0 4px 14px rgba(223, 155, 62, 0.35)',
              transition: 'transform 0.15s ease'
            }}
          >
            <Gift size={18} />
            <span>{isClaiming ? "Récupération en cours..." : `Réclamer mon butin (+${rewardCoins} 🪙) !`}</span>
          </button>
        )
      ) : isRaidExpired ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid var(--border-color, #2f2c3d)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-secondary, #9a94ab)'
        }}>
          <span>💀 Le boss n'a pas été vaincu à temps et s'est enfui. Rendez-vous au prochain raid !</span>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.45rem',
          backgroundColor: hasEligibleWorkouts ? 'rgba(110, 181, 131, 0.1)' : 'rgba(251, 191, 36, 0.08)',
          border: hasEligibleWorkouts ? '1px solid rgba(110, 181, 131, 0.25)' : '1px solid rgba(251, 191, 36, 0.2)',
          padding: '0.55rem 0.75rem',
          borderRadius: '10px',
          fontSize: '0.76rem',
          color: 'var(--text-primary, #f0eef5)'
        }}>
          <Gift size={16} color={hasEligibleWorkouts ? "var(--secondary-color, #6eb583)" : "#fbbf24"} />
          <span>
            {hasEligibleWorkouts 
              ? <>✓ Tu es <strong>éligible</strong> aux <strong>+{rewardCoins} 🪙</strong> de butin collectif !</>
              : <>Butin collectif : <strong>+{rewardCoins} 🪙</strong> (1 séance requise pour débloquer)</>}
          </span>
        </div>
      )}
    </div>
  );

  const renderTopHuntersCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box'
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trophy size={18} color="#fbbf24" />
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800 }}>Top meilleurs chasseurs</h3>
          </div>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #9a94ab)' }}>
            {bossCycle.isRestPeriod ? 'Bilan du raid' : 'Endurance hebdo'}
          </span>
        </div>

        {/* LISTE TOP 3 DES ATHLÈTES RÉELS */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {topHunters.length === 0 ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', padding: '0.5rem 0' }}>
              Aucun athlète n'a encore enregistré de séance cette semaine.
            </div>
          ) : (
            topHunters.map(hunter => (
              <div 
                key={hunter.rank || hunter.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '0.5rem 0.7rem', 
                  backgroundColor: 'rgba(255,255,255,0.03)', 
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                  <span style={{ fontSize: '1rem' }}>{hunter.medal}</span>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>{hunter.name}</div>
                    <div style={{ fontSize: '0.68rem', color: hunter.isKing ? '#fbbf24' : 'var(--primary-color-hover, #a89fcf)', fontWeight: 600 }}>
                      {hunter.title}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
                  {(hunter.damage || 0).toLocaleString()} {damageUnit}
                </span>
              </div>
            ))
          )}

          {/* BANNIÈRE DE L'UTILISATEUR CONNECTÉ */}
          <div style={{
            marginTop: '0.45rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.65rem 0.75rem',
            backgroundColor: 'rgba(140, 127, 191, 0.14)',
            border: '1.5px solid var(--primary-color, #8c7fbf)',
            borderRadius: '12px',
            boxShadow: '0 0 16px rgba(140, 127, 191, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{
                fontSize: '0.72rem',
                fontWeight: 900,
                backgroundColor: 'var(--primary-color, #8c7fbf)',
                color: '#fff',
                padding: '0.18rem 0.45rem',
                borderRadius: '6px'
              }}>
                {userRank ? `#${userRank}` : '-'}
              </div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary, #f0eef5)' }}>
                  Toi ({userName})
                </div>
                <div style={{
                  fontSize: '0.68rem',
                  color: hasEligibleWorkouts ? 'var(--secondary-color, #6eb583)' : 'var(--text-secondary, #9a94ab)',
                  fontWeight: 600
                }}>
                  {isDead ? (userPart?.hasClaimedReward ? "✓ Butin récupéré" : `🎁 Butin prêt (+${rewardCoins} 🪙)`) : (hasEligibleWorkouts ? `✓ Éligible au Butin (+${rewardCoins} 🪙)` : `0 séance (non éligible)`)}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 900, color: 'var(--primary-color-hover, #a89fcf)' }}>
                {myDamage.toLocaleString()} {damageUnit}
              </span>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary, #9a94ab)' }}>
                {userRank ? `Rang #${userRank}` : 'Non classé'}
              </div>
            </div>
          </div>

        </div>
      </div>
      
      <div style={{ marginTop: '0.8rem', fontSize: '0.7rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.5rem' }}>
        Le Top 3 et le coup final remportent les titres de prestige à la défaite du boss.
      </div>
    </div>
  );

  const renderPrestigeTitlesCard = () => (
    <div style={{
      backgroundColor: 'var(--bg-card, #1c1a24)',
      border: '1px solid var(--border-color, #2f2c3d)',
      borderRadius: '16px',
      padding: '1.2rem',
      boxShadow: 'var(--shadow)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '0.5rem',
      boxSizing: 'border-box'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-secondary, #9a94ab)' }}>
        <Crown size={16} color="#fbbf24" /> Titres en jeu (7 jours)
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>👑 <strong>Top 1</strong></span>
          <span style={{ color: '#fbbf24', fontWeight: 700 }}>Fléau des Titans</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>⚔️ <strong>Top 2-3</strong></span>
          <span style={{ color: 'var(--primary-color-hover, #a89fcf)', fontWeight: 700 }}>Champion du Raid</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.76rem' }}>
          <span>🎯 <strong>Dernier Coup</strong></span>
          <span style={{ color: 'var(--secondary-color, #6eb583)', fontWeight: 700 }}>Coup de Grâce</span>
        </div>
      </div>

      <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary, #9a94ab)', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.4rem' }}>
        Attribués directement sur le profil dès la mort du boss (actifs jusqu'au prochain boss).
      </div>
    </div>
  );

  return (
    <div className={`boss-arena-wrapper ${screenShake ? 'shake-screen' : ''}`} style={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      color: 'var(--text-primary, #f0eef5)',
      paddingBottom: '2.5rem',
      position: 'relative'
    }}>
      <style>{`
        @keyframes golemBreathing {
          0%, 100% { transform: scale(1) translateY(0px); }
          50% { transform: scale(1.025) translateY(-4px); }
        }
        @keyframes lightningArc {
          0%, 100% { filter: drop-shadow(0 0 4px #a855f7) drop-shadow(0 0 10px rgba(140, 127, 191, 0.4)); }
          50% { filter: drop-shadow(0 0 8px #fbbf24) drop-shadow(0 0 18px rgba(168, 85, 247, 0.7)); }
        }
        @keyframes eyeGlintThor {
          0%, 100% { filter: drop-shadow(0 0 3px #fbbf24) drop-shadow(0 0 7px #a855f7); opacity: 0.95; }
          50% { filter: drop-shadow(0 0 8px #fbbf24) drop-shadow(0 0 15px #f59e0b); opacity: 1; }
        }
        @keyframes damagePop {
          0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.6); }
          20% { opacity: 1; transform: translate(var(--dx), calc(var(--dy) - 20px)) scale(1.15); }
          100% { opacity: 0; transform: translate(var(--dx), calc(var(--dy) - 55px)) scale(0.9); }
        }
        .shake-screen {
          animation: shake 0.35s cubic-bezier(.36,.07,.19,.97) both;
        }
        @keyframes shake {
          10%, 90% { transform: translate3d(-1px, 0, 0); }
          20%, 80% { transform: translate3d(2px, 0, 0); }
          30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
          40%, 60% { transform: translate3d(4px, 0, 0); }
        }
        .golem-container {
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.08s ease;
        }
        .golem-container:active {
          transform: scale(0.96) !important;
        }
        .golem-container.hit svg {
          filter: brightness(2) contrast(1.5) drop-shadow(0 0 25px #ffffff) !important;
          transition: filter 0.05s ease;
        }
        .boss-title-strikethrough {
          text-decoration: line-through 2px #ef4444 !important;
          -webkit-text-decoration-line: line-through !important;
          -webkit-text-decoration-color: #ef4444 !important;
          -webkit-text-decoration-style: solid !important;
          text-decoration-color: #ef4444 !important;
          text-decoration-thickness: 2px !important;
          color: #94a3b8 !important;
          opacity: 0.8 !important;
          margin: 0 4px !important;
          font-weight: 700 !important;
          display: inline-block !important;
        }

        /* --- LAYOUT MOBILE PAR DÉFAUT (PARFAIT SUR TÉLÉPHONE) --- */
        .boss-main-layout {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          width: 100%;
        }
        .boss-arena-box {
          width: 100%;
          box-sizing: border-box;
        }
        .boss-arena-center {
          width: 100%;
          margin: 0.2rem auto 0.5rem auto;
        }
        .dashboard-cards-col {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: 100%;
        }

        /* --- LAYOUT PC / GRAND ÉCRAN (2 COLONNES HARMONIEUSES) --- */
        @media (min-width: 901px) {
          .boss-main-layout {
            display: grid !important;
            grid-template-columns: 1.15fr 0.85fr !important;
            gap: 1.5rem !important;
            align-items: stretch !important;
          }
          .boss-arena-box {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            padding: 1.8rem 1.4rem !important;
            box-sizing: border-box !important;
          }
          .boss-arena-top {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            width: 100% !important;
          }
          .boss-arena-center {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 280px !important;
            margin: 1rem 0 !important;
            width: 100% !important;
          }
          .dashboard-cards-col {
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
            height: 100% !important;
            justify-content: space-between !important;
          }
        }
      `}</style>

      {/* Halo lumineux d'ambiance violet officiel (identique à Dracofonte et au Barromancien) */}
      <div style={{
        position: 'absolute',
        top: '0%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(140, 127, 191, 0.14) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ width: '100%', position: 'relative', zIndex: 1, boxSizing: 'border-box' }}>

        {/* NAVIGATION HAUTE AVEC SWITCHER DE BOSS */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: '1rem', 
          width: '100%',
          flexWrap: 'wrap',
          gap: '0.6rem'
        }}>
          <button 
            onClick={() => navigate('/social?tab=boss')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              backgroundColor: 'var(--bg-card, #1c1a24)', border: '1px solid var(--border-color, #2f2c3d)',
              color: 'var(--text-secondary, #9a94ab)', padding: '0.45rem 0.85rem', borderRadius: '10px',
              cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              boxShadow: 'var(--shadow)'
            }}
          >
            <ChevronLeft size={16} /> Retour au Social
          </button>

          {/* Switcher d'arène Boss */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: 'var(--bg-card, #1c1a24)',
            border: '1px solid var(--border-color, #2f2c3d)',
            borderRadius: '12px',
            padding: '3px',
            gap: '4px'
          }}>
            <button
              onClick={() => navigate('/test2')}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary, #9a94ab)',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🧙 Barromancien (Reps)
            </button>
            <button
              style={{
                border: 'none',
                background: 'rgba(140, 127, 191, 0.25)',
                color: 'var(--primary-color-hover, #a89fcf)',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.78rem',
                fontWeight: 800,
                cursor: 'default'
              }}
            >
              🗿 MusculaThor (Temps)
            </button>
          </div>
        </div>

        {/* CONTENU PRINCIPAL : LAYOUT 2 COLONNES HARMONIEUX */}
        <div className="boss-main-layout">

          {/* COLONNE GAUCHE : ARÈNE DU BOSS */}
          <div className="boss-arena-box" style={{
            backgroundColor: 'var(--bg-card, #1c1a24)',
            border: isLowHp 
              ? '1.5px solid var(--danger-color, #ef4444)' 
              : '1px solid var(--border-color, #2f2c3d)',
            boxShadow: isLowHp 
              ? '0 0 20px rgba(239, 68, 68, 0.22), var(--shadow)' 
              : 'var(--shadow)',
            borderRadius: '20px',
            padding: '1.3rem 1rem',
            textAlign: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            transition: 'border 0.3s ease, box-shadow 0.3s ease'
          }}>

            <div className="boss-arena-top" style={{ width: '100%' }}>
              {/* BADGES & STATUT DU BOSS */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: isDead 
                    ? 'rgba(110, 181, 131, 0.18)' 
                    : 'rgba(140, 127, 191, 0.18)',
                  color: isDead 
                    ? 'var(--secondary-color, #6eb583)' 
                    : 'var(--primary-color-hover, #a89fcf)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px'
                }}>
                  {isDead ? '🏆 Boss Vaincu' : '⚔️ Évènement Spécial'}
                </span>

                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  padding: '0.2rem 0.55rem',
                  backgroundColor: 'rgba(110, 181, 131, 0.12)',
                  color: 'var(--secondary-color, #6eb583)',
                  borderRadius: '20px',
                  fontSize: '0.68rem',
                  fontWeight: 700
                }}>
                  ⚖️ {boss?.ruleLabel || '1h30 de séance = 1 300 dégâts'}
                </span>
              </div>

              {/* NOM DU BOSS */}
              <h1 style={{
                margin: '0.2rem 0 0.15rem 0',
                fontSize: '1.9rem',
                fontWeight: 900,
                letterSpacing: '-0.5px',
                color: 'var(--text-primary, #f0eef5)'
              }}>
                {boss.name || 'MusculaThor'}
              </h1>
              <p style={{
                margin: '0 0 0.9rem 0',
                fontSize: '0.82rem',
                color: 'var(--text-secondary, #9a94ab)',
                fontStyle: 'italic'
              }}>
                {renderBossTitle(boss?.title || 'Le Dieu de la ~~Foudre~~ Fonte')}
              </p>

              {/* BARRE DE VIE RÉACTOR (LIVE SYNC & INSTANT FEEDBACK) */}
              <div style={{ maxWidth: '440px', margin: '0 auto 0.75rem auto', width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.35rem', fontSize: '0.78rem' }}>
                  <span style={{ color: isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-secondary, #9a94ab)', fontWeight: 600, transition: 'color 0.3s ease' }}>Points de Vie</span>
                  <span style={{ fontWeight: 900, color: isDead ? '#6b7280' : (isLowHp ? 'var(--danger-color, #ef4444)' : 'var(--text-primary, #f0eef5)'), transition: 'color 0.3s ease' }}>
                    {currentHp.toLocaleString()} / {maxHp.toLocaleString()} PV ({hpPercent.toFixed(1)}%)
                  </span>
                </div>

                <div style={{
                  width: '100%',
                  height: '14px',
                  backgroundColor: 'rgba(0, 0, 0, 0.35)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #2f2c3d)',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)'
                }}>
                  <div style={{
                    width: `${hpPercent}%`,
                    height: '100%',
                    background: isDead
                      ? 'var(--text-secondary, #6b7280)'
                      : isLowHp
                        ? 'linear-gradient(90deg, #ec4899 0%, #f43f5e 100%)'
                        : 'linear-gradient(90deg, #6d28d9 0%, var(--primary-color, #8c7fbf) 50%, var(--secondary-color, #6eb583) 100%)',
                    borderRadius: '8px',
                    transition: 'width 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)'
                  }} />
                </div>
              </div>
            </div>

            {/* CENTRE DU BLOC : ILLUSTRATION DE MUSCULATHOR (COULEURS THÈME APP & ÉCLAIRS DE THOR) */}
            <div className="boss-arena-center">
              <div
                className={`golem-container ${isHit ? 'hit' : ''}`}
                onClick={handleBossTap}
                title={isDead ? "Le titan a été terrassé !" : "Tapote avec ton doigt ou ta souris pour frapper !"}
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '320px',
                  margin: '0.2rem auto 0.5rem auto',
                  filter: isDead ? 'grayscale(1) opacity(0.35)' : 'none',
                  transition: 'filter 0.3s'
                }}
              >
                {/* SVG ILLUSTRATION DE MUSCULATHOR */}
                <svg
                  viewBox="0 0 360 360"
                  style={{
                    width: '100%',
                    height: 'auto',
                    overflow: 'visible',
                    animation: isDead ? 'none' : 'golemBreathing 4s ease-in-out infinite'
                  }}
                >
                  <defs>
                    {/* Filtre Lightning & Sparkle */}
                    <filter id="thorGlow" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="5" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>
                    <filter id="arcGlow" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur stdDeviation="4" result="blur" />
                      <feComposite in="SourceGraphic" in2="blur" operator="over" />
                    </filter>

                    {/* Dégradés Pierre Basalte Sombre & Granite Métallique */}
                    <linearGradient id="basaltRock" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3f3f46" />
                      <stop offset="50%" stopColor="#27272a" />
                      <stop offset="100%" stopColor="#18181b" />
                    </linearGradient>

                    <linearGradient id="darkStone" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#27272a" />
                      <stop offset="60%" stopColor="#18181b" />
                      <stop offset="100%" stopColor="#09090b" />
                    </linearGradient>

                    <linearGradient id="highlightRock" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#52525b" />
                      <stop offset="50%" stopColor="#71717a" />
                      <stop offset="100%" stopColor="#3f3f46" />
                    </linearGradient>

                    {/* Dégradé Fissures Telluriques Éclair Violet / Or d'Asgard */}
                    <linearGradient id="thorEnergy" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fbbf24" />
                      <stop offset="50%" stopColor="#c084fc" />
                      <stop offset="100%" stopColor="#8c7fbf" />
                    </linearGradient>

                    {/* Dégradé Barre Olympique Acier Lourd */}
                    <linearGradient id="olympicBar" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#475569" />
                      <stop offset="50%" stopColor="#94a3b8" />
                      <stop offset="100%" stopColor="#334155" />
                    </linearGradient>

                    {/* Dégradé Disque de fonte 25kg noir et violet arcanique */}
                    <linearGradient id="plateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1e293b" />
                      <stop offset="50%" stopColor="#334155" />
                      <stop offset="100%" stopColor="#0f172a" />
                    </linearGradient>
                  </defs>

                  {/* 1. CHOC TELLURIQUE AU SOL & ÉTINCELLES D'ARCS ÉLECTRIQUES */}
                  <g style={{ opacity: isDead ? 0.2 : 0.85 }}>
                    <ellipse cx="180" cy="335" rx="140" ry="22" fill="rgba(0,0,0,0.55)" filter="blur(4px)" />
                    {/* Fissures au sol dorées et violettes */}
                    <path d="M 80 335 L 120 338 L 155 332 L 180 337 L 220 333 L 260 338" stroke="url(#thorEnergy)" strokeWidth="2.5" fill="none" opacity="0.75" filter="url(#arcGlow)" />
                    <path d="M 120 338 L 135 348" stroke="#fbbf24" strokeWidth="1.5" fill="none" opacity="0.6" />
                    <path d="M 220 333 L 235 345" stroke="#a855f7" strokeWidth="1.5" fill="none" opacity="0.6" />

                    {/* Fragments rocheux orbitants avec reflets violets */}
                    <polygon points="50,110 62,105 66,118 54,124" fill="#3f3f46" stroke="#c084fc" strokeWidth="1" />
                    <polygon points="310,130 322,122 328,136 316,142" fill="#3f3f46" stroke="#fbbf24" strokeWidth="1" />
                    <polygon points="40,240 50,234 53,245 42,249" fill="#27272a" stroke="#8c7fbf" strokeWidth="1" />
                    <polygon points="315,250 326,244 330,256 319,260" fill="#27272a" stroke="#8c7fbf" strokeWidth="1" />
                  </g>

                  {/* 2. TRAPÈZES COLOSSAUX DE TITAN */}
                  <path d="M 115 150 C 110 110 130 65 170 65 L 180 90 L 140 160 Z" fill="url(#basaltRock)" stroke="#18181b" strokeWidth="2" />
                  <path d="M 245 150 C 250 110 230 65 190 65 L 180 90 L 220 160 Z" fill="url(#basaltRock)" stroke="#18181b" strokeWidth="2" />

                  {/* 3. BARRE OLYMPIQUE TORDUE SUR SES ÉPAULES */}
                  <path d="M 35 125 Q 180 155 325 125" stroke="url(#olympicBar)" strokeWidth="9" strokeLinecap="round" fill="none" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.6))" />
                  {/* Disques de fonte 25kg olympiques */}
                  <rect x="25" y="105" width="14" height="42" rx="3" fill="url(#plateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="1.5" />
                  <rect x="14" y="112" width="10" height="28" rx="2" fill="#09090b" stroke="#71717a" strokeWidth="1" />
                  <rect x="321" y="105" width="14" height="42" rx="3" fill="url(#plateGrad)" stroke="var(--primary-color, #8c7fbf)" strokeWidth="1.5" />
                  <rect x="336" y="112" width="10" height="28" rx="2" fill="#09090b" stroke="#71717a" strokeWidth="1" />

                  {/* 4. DORSAL & MASSE ROCHEUSE DU DOS */}
                  <path d="M 100 170 C 70 200 65 260 85 300 L 275 300 C 295 260 290 200 260 170 Z" fill="url(#darkStone)" stroke="#09090b" strokeWidth="3" />

                  {/* 5. DELTOÏDES ROCHEUX MASSIFS */}
                  {/* Deltoïde gauche */}
                  <g>
                    <path d="M 60 160 C 50 125 90 105 120 115 C 130 140 120 175 90 185 C 75 185 65 175 60 160 Z" fill="url(#basaltRock)" stroke="#09090b" strokeWidth="2.5" />
                    <path d="M 80 125 L 92 145 L 85 165" stroke="url(#thorEnergy)" strokeWidth="2.5" fill="none" filter="url(#arcGlow)" />
                  </g>
                  {/* Deltoïde droit */}
                  <g>
                    <path d="M 300 160 C 310 125 270 105 240 115 C 230 140 240 175 270 185 C 285 185 295 175 300 160 Z" fill="url(#basaltRock)" stroke="#09090b" strokeWidth="2.5" />
                    <path d="M 280 125 L 268 145 L 275 165" stroke="url(#thorEnergy)" strokeWidth="2.5" fill="none" filter="url(#arcGlow)" />
                  </g>

                  {/* 6. BRAS & POIGNETS EN FER FORGÉ */}
                  {/* Bras gauche */}
                  <path d="M 65 175 C 45 205 40 245 55 275 C 70 270 85 255 90 230 C 95 200 85 180 65 175 Z" fill="url(#basaltRock)" stroke="#09090b" strokeWidth="2" />
                  <rect x="42" y="260" width="28" height="15" rx="3" fill="#18181b" stroke="#71717a" strokeWidth="2" transform="rotate(-15 56 267)" />
                  <path d="M 45 275 L 38 290 L 44 298" stroke="#94a3b8" strokeWidth="2.5" fill="none" />

                  {/* Bras droit */}
                  <path d="M 295 175 C 315 205 320 245 305 275 C 290 270 275 255 270 230 C 265 200 275 180 295 175 Z" fill="url(#basaltRock)" stroke="#09090b" strokeWidth="2" />
                  <rect x="290" y="260" width="28" height="15" rx="3" fill="#18181b" stroke="#71717a" strokeWidth="2" transform="rotate(15 304 267)" />
                  <path d="M 315 275 L 322 290 L 316 298" stroke="#94a3b8" strokeWidth="2.5" fill="none" />

                  {/* 7. PECTORAUX ROCHEUX EN RELIEF */}
                  <path d="M 125 150 L 175 155 L 173 205 C 150 215 120 205 110 185 Z" fill="url(#highlightRock)" stroke="#18181b" strokeWidth="2" />
                  <path d="M 235 150 L 185 155 L 187 205 C 210 215 240 205 250 185 Z" fill="url(#highlightRock)" stroke="#18181b" strokeWidth="2" />

                  {/* Fissures centrales d'éclair violet/or entre les pecs */}
                  <path d="M 180 148 L 180 210 L 175 230 L 185 250 L 180 275" stroke="url(#thorEnergy)" strokeWidth="3" fill="none" filter="url(#arcGlow)" />
                  <path d="M 180 180 L 155 190 L 138 185" stroke="#a855f7" strokeWidth="2" fill="none" filter="url(#arcGlow)" />
                  <path d="M 180 180 L 205 190 L 222 185" stroke="#fbbf24" strokeWidth="2" fill="none" filter="url(#arcGlow)" />

                  {/* 8. ABDOMINAUX EN BLOCS DE GRANITE TAILLÉS */}
                  <rect x="145" y="215" width="30" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />
                  <rect x="185" y="215" width="30" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />
                  <rect x="148" y="238" width="28" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />
                  <rect x="184" y="238" width="28" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />
                  <rect x="152" y="260" width="25" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />
                  <rect x="183" y="260" width="25" height="18" rx="4" fill="#27272a" stroke="#18181b" strokeWidth="1.5" />

                  {/* Ceinture de force d'haltérophile en cuir sombre et boucle dorée */}
                  <rect x="135" y="285" width="90" height="20" rx="4" fill="#2e1065" stroke="#581c87" strokeWidth="2" />
                  <rect x="168" y="280" width="24" height="30" rx="5" fill="#18181b" stroke="#fbbf24" strokeWidth="2.5" />
                  <circle cx="180" cy="295" r="4" fill="#fbbf24" filter="url(#arcGlow)" />

                  {/* 9. TÊTE & VISAGE DE MUSCULATHOR (MÂCHOIRE DE GRANITE, YEUX D'ÉCLAIR) */}
                  <path d="M 155 75 L 205 75 L 215 95 L 208 135 L 180 145 L 152 135 L 145 95 Z" fill="url(#basaltRock)" stroke="#09090b" strokeWidth="2.5" />
                  <polygon points="150,95 210,95 205,105 155,105" fill="#18181b" stroke="#09090b" strokeWidth="1.5" />

                  {/* Fissure de tonnerre sur le front */}
                  <path d="M 180 75 L 185 88 L 178 95" stroke="#fbbf24" strokeWidth="2" fill="none" filter="url(#arcGlow)" />

                  {/* Yeux d'éclair doré incandescents de Thor */}
                  <ellipse cx="166" cy="106" rx="6" ry="3.5" fill="#fbbf24" filter="url(#thorGlow)" style={{ animation: isDead ? 'none' : 'eyeGlintThor 2.5s ease-in-out infinite' }} />
                  <ellipse cx="194" cy="106" rx="6" ry="3.5" fill="#fbbf24" filter="url(#thorGlow)" style={{ animation: isDead ? 'none' : 'eyeGlintThor 2.5s ease-in-out infinite' }} />
                  <line x1="166" y1="103" x2="166" y2="109" stroke="#78350f" strokeWidth="1.5" />
                  <line x1="194" y1="103" x2="194" y2="109" stroke="#78350f" strokeWidth="1.5" />

                  {/* Nez de roc */}
                  <polygon points="180,105 184,118 176,118" fill="#18181b" />

                  {/* Dents serrées de pierre */}
                  <rect x="165" y="124" width="30" height="7" rx="2" fill="#09090b" stroke="#3f3f46" strokeWidth="1" />
                  <line x1="172" y1="124" x2="172" y2="131" stroke="#a1a1aa" strokeWidth="1.2" />
                  <line x1="180" y1="124" x2="180" y2="131" stroke="#a1a1aa" strokeWidth="1.2" />
                  <line x1="188" y1="124" x2="188" y2="131" stroke="#a1a1aa" strokeWidth="1.2" />
                </svg>

                {/* DÉGÂTS FLOTTANTS (EXACTEMENT HARMONISÉS AVEC LE RESTE DE L'APP) */}
                {floatingDamages.map(item => (
                  <div
                    key={item.id}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      '--dx': `${item.x}px`,
                      '--dy': `${item.y}px`,
                      animation: 'damagePop 0.9s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards',
                      pointerEvents: 'none',
                      fontSize: item.isTap ? '0.95rem' : (item.isCrit ? '1.45rem' : '1.15rem'),
                      fontWeight: 900,
                      color: item.isTap ? '#fbbf24' : (item.isCrit ? '#ef4444' : 'var(--primary-color-hover, #a89fcf)'),
                      textShadow: '0 2px 10px rgba(0,0,0,0.95), 0 0 14px rgba(140, 127, 191, 0.8)',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}
                  >
                    {item.text}
                  </div>
                ))}
              </div>
            </div>

            {/* BAS DU BLOC : TAUNT & INDICATION */}
            <div className="boss-arena-bottom" style={{ width: '100%' }}>
              {/* TAUNT DU BOSS */}
              <div style={{
                fontSize: '0.82rem',
                color: isDead ? '#6b7280' : 'var(--text-secondary, #9a94ab)',
                fontWeight: 700,
                margin: '0.4rem 0 0.8rem 0'
              }}>
                « {isDead 
                  ? (boss.killer ? `Vaincu par ${boss.killer.name} et toute la commu !` : 'Argh... La commu a vaincu !')
                  : (boss.taunt || "Ceinture, sangles, magnésie... tout ce cinéma pour une barre aussi légère ?")} »
              </div>

              {/* INDICATION DE COMBAT NATURELLE */}
              {!isDead && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  padding: '0.5rem 0.85rem',
                  borderRadius: '12px',
                  fontSize: '0.74rem',
                  color: 'var(--text-secondary, #9a94ab)',
                  lineHeight: 1.4,
                  maxWidth: '92%'
                }}>
                  <span>💡 Tapote avec ton doigt ou ta souris pour frapper, ou termine tes vraies séances en salle pour lui infliger de lourds dégâts d'endurance (1h30 = 1 300 dégâts) !</span>
                </div>
              )}
            </div>

          </div>

          {/* COLONNE DROITE : STATS & CLASSEMENT */}
          <div className="dashboard-cards-col">
            {renderContributionCard()}
            {renderTopHuntersCard()}
            {renderPrestigeTitlesCard()}
          </div>

        </div>

      </div>
    </div>
  );
}
